import './canvasGate.css';
import React, { useRef, CSSProperties, useEffect, useCallback, useState, useMemo, useContext } from 'react';
import {
	CustomWidgetState,
	WidgetGroupState,
	WidgetState,
	WidgetType,
	HubServiceState,
} from '@kemu-io/kemu-core/types';
import { useContextMenu } from 'react-contexify';
import { useIntl } from 'react-intl';
import { SettingFilled } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Connection } from '@jsplumb/community/types/core';
import { SourceInfo } from '@kemu-io/kemu-core/common/eventHistory';
import { findWidgetInRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { NoteWidgetState } from '@kemu-io/kemu-core/widgets/note/index.js';
import { getGateBody, PortDescription } from '../gates/index';
import GateSettingsCloseBtn from '../gateSettingsCloseBtn/gateSettingsCloseBtn';
import { repaintPorts } from './utils';
import WidgetLabels, { LabelsInstanceRef } from './WidgetLabels/WidgetLabels';
import styles from './canvasGate.module.css';
import WidgetHelpBar from './WidgetHelpBar/WidgetHelpBar';
import { MENU_GATE, WidgetMenuProps } from '@src/features/LogicMapper/contextMenu/contextMenu';
import { generateDomId } from '@common/utils';
import Logger from '@common/logger';
import useReactiveWidgetState from '@common/hooks/useReactiveWidgetState';
import { CANVAS_WIDGET_CLASS, CUSTOM_HUB_SERVICE_UI_CLASS, DISABLED_WIDGET_CLASS, WIDGET_INVOKED_CLASS, WIDGET_ABORT_DRAG_SELECTION } from '@common/constants';
import { KemuConnectionData, StatelessRecipeWidget } from '@src/types/core_t';
import { getCategoryFromType } from '@common/widgetCategories';
import { selectCurrentRecipeType } from '@src/features/Workspace/workspaceSlice';
import useWidgetInvokeMonitor from '@hooks/useWidgetInvokeMonitor';
import useHubServiceInfo from '@hooks/useHubServiceInfo';
import WidgetProcessingWrapper from '@components/WidgetProcessingWrapper/WidgetProcessingWrapper';
import { LogicMapperContext, useLogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import useGetWidgetsMap from '@hooks/useGetWidgetsMap';
import useWidgetRunModeStatus from '@common/hooks/useWidgetRunModeStatus';

const logger = Logger('canvasGate');
interface Props{
	thingRecipeId: string;
	thingDbId: string;
	thingVersion: string;
	recipeId: string;
	gateId: string;
	hidden: boolean;
	/**
	 * Used to notify parent ports for this widget have been painted
	 * @returns true if connections have been painted.
	 */
	// onPortsPainted: (widgetId: string) => boolean;
}

type PortWithoutType = Omit<PortDescription, 'type'>;

const settingsDialogAnimation = {
	visible: {
		opacity: 1,
		scale: 1,
		transition: {
			duration: 0.1
		},
	},

	hide: {
		opacity: 0,
		scale: 0,
		transition: {
			duration: 0.1
		},
	},

	hidden: {
		opacity: 0,
		scale: 0.9,
	}
};


const checkIfSourceIsInnerWidget = (recipeId: string, thingRecipeId: string, sourceWidgetId: string, currentWidgetGroupId: string) => {
	const sourceWidgetInfo = findWidgetInRecipe(recipeId, thingRecipeId, sourceWidgetId);
	const isChildWidget = sourceWidgetInfo?.groupId && sourceWidgetInfo?.groupId === currentWidgetGroupId;
	return isChildWidget;
};

const CanvasGateElement = (props: Props & { gateInfo: StatelessRecipeWidget }): React.JSX.Element => {
	const intl = useIntl();
	const { jsPlumb: plumbInstance, drawPortsAndConnections, removePortsAndConnections } = useLogicMapperContext();
	const [showSettingsBox, setShowSettingsBox] = useState(false);
	const gateElRef = useRef<HTMLDivElement>(null);
	const getWidgetsMap = useGetWidgetsMap();
	const { onShowHelp } = useContext(LogicMapperContext);
	const widgetLabelsRef = useRef<LabelsInstanceRef>(null);
	const { gateInfo, gateId, thingRecipeId, recipeId, hidden } = props;
	const currentRecipeType = useSelector(selectCurrentRecipeType);
	const [dynamicWrapperClasses, setDynamicWrapperClasses] = useState<string[]>([]);
	const gateUI = getGateBody(gateInfo.type);
	if (!gateUI) { throw Error(`Unknown gate [${gateInfo.type}]`); }

	const { type } = gateInfo;
	const hasCustomSettings = !!gateUI.CustomSettingsDialog;

	// TODO: By default the useGateState should ignore private properties to prevent re-rendering
	// when widget processors use state flags internally (Eg. pixelfy widget)
	const [gateState] = useReactiveWidgetState<CustomWidgetState>(props.recipeId, thingRecipeId, props.gateId);
	const canvasDomId = generateDomId(props.recipeId, thingRecipeId, props.gateId);
	const getHubServiceInfo = useHubServiceInfo();

	const hubService = useMemo(() => {
		if (type === WidgetType.hubService) {
			const hubState = gateState as WidgetState<HubServiceState>;
			if (hubState.service) {
				return getHubServiceInfo(hubState.service.name, hubState.service.version);
			}
		}

		return null;
	}, [getHubServiceInfo, gateState, type]);

	const serviceOffline = type === WidgetType.hubService && !hubService;

	// Determine if widget should be highlighted in run mode (new or changed)
	const runModeStatus = useWidgetRunModeStatus(
		recipeId,
		thingRecipeId,
		gateId,
		gateState,
		gateInfo.canvas,
		gateInfo.children
	);

	const handleWidgetInvoked = useCallback((wasInvoked: boolean, eventSource?: SourceInfo, aborted?: boolean) => {
		const connections = plumbInstance?.getConnections({ target: gateElRef.current }) as Connection[];
		for (const conn of connections) {
			// Cancel animations
			if (aborted) {
				conn.removeClass(WIDGET_INVOKED_CLASS);
				continue;
			}

			const connData: KemuConnectionData | undefined = conn.getData();
			if (connData && eventSource?.widgetId) {

				// For groups, the source widget will be one of the inner widgets
				// in which case, this initial conditional doesn't work.
				if (connData?.sourceWidgetId === eventSource?.widgetId
					// if the source is a widget that belongs to a group, we need to find out if the group
					// is a child of connection source.
					|| checkIfSourceIsInnerWidget(recipeId, thingRecipeId, eventSource.widgetId, gateId)
				) {
					if (wasInvoked) {
						conn.addClass(WIDGET_INVOKED_CLASS);
					} else {
						conn.removeClass(WIDGET_INVOKED_CLASS);
					}
				}
			}
		}
	}, [plumbInstance, thingRecipeId, recipeId, gateId]);

	useWidgetInvokeMonitor(props.thingRecipeId, props.gateId, 200, handleWidgetInvoked);

	// Get actual presentation information from the gate itself
	const gatePorts = gateUI.getPortsInformation!(gateState, {
		id: gateInfo.id,
		thingRecipeId: thingRecipeId,
		thingDbId: props.thingDbId,
		thingVersion: props.thingVersion,
		recipeId: recipeId,
		recipePoolId: recipeId,
		recipeType: currentRecipeType,
	}, intl);

	// The color of the hub service is the color of the group
	const isHubServiceVariant =
    type === WidgetType.hubService && (gateState as HubServiceState).variantId;
  let hubServiceColor =
    hubService?.color || (gateState as HubServiceState)?.service?.color;

  // Service variants can define their own color
  if (isHubServiceVariant) {
    const matchingVariant = (
      gateState as HubServiceState
    ).service?.variants?.find(
      (v) => v.id === (gateState as HubServiceState).variantId
    );
    if (matchingVariant) {
      // If the variant has a color, use it, otherwise use the service color
      hubServiceColor = matchingVariant.color || hubService?.color;
    }
  }


	const handleRedrawPorts = useCallback(() => {
		if (!plumbInstance) { return; }
		const gateInfo = getWidgetsMap()[gateId];
		if(gateInfo){
			const widgets = {
				[gateId]: gateInfo,
			};

			removePortsAndConnections(widgets);
			drawPortsAndConnections(widgets);
		}
	}, [plumbInstance, gateId, drawPortsAndConnections, removePortsAndConnections]);

  const handleRepaintPorts = useCallback(() => {
		if (!plumbInstance) { return; }
    repaintPorts(plumbInstance, gateElRef.current);
  }, [plumbInstance]);

  const gatePos: CSSProperties = {
    left: gateInfo.canvas.position.x,
    top: gateInfo.canvas.position.y,
    ...(gateInfo.type === WidgetType.widgetGroup &&
    (gateState as WidgetGroupState)?.color
      ? {
          boxShadow: `0px 0px 7px 1px ${
            (gateState as WidgetGroupState)?.color
          }`,
        }
      : {}),
    ...(gateInfo.type === WidgetType.hubService && hubServiceColor
      ? {
          boxShadow: `0px 0px 7px 1px ${hubServiceColor}`,
        }
      : {}),

    ...(gateUI.getSelectionColor && {
      boxShadow: `0px 0px 7px 1px ${gateUI.getSelectionColor(
        gateState as Readonly<WidgetState>
      )}`,
    }),

    // Highlight widgets that are new or have changed state/position/connections in run mode
    // For hub services, the shadow is applied via CSS to the inner .k-hs-wrap container
    ...(runModeStatus.shouldHighlight && type !== WidgetType.hubService && {
      boxShadow: runModeStatus.highlightType === 'position'
        ? '0px 0px 3px 3px var(--kemu-color-widget-run-mode-position)'
        : '0px 0px 3px 3px var(--kemu-color-widget-run-mode-changed)',
    }),
  };

	const isLockedNote = gateInfo.type === WidgetType.note && (gateState as NoteWidgetState).locked;

	// Shows the context menu
	const { show: showMenu } = useContextMenu({ id: MENU_GATE });
	const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
		// @ts-expect-error not sure why tagName isn't recognized
		if (event.target.tagName !== 'INPUT') {
			event.preventDefault();
			showMenu(event, {
				props: {
					disabled: gateInfo.disabled,
					boundaryClass: CANVAS_WIDGET_CLASS,
					recipeId,
					gateId,
					blockId: thingRecipeId,
					gateType: gateInfo.type,
					returnOriginalEvent: gateInfo.returnOriginalEvent
				} as WidgetMenuProps
			});
		}
	};

	const onShowCustomSettings = useCallback(() => {
		plumbInstance?.getEndpoints(gateElRef.current).forEach(ep => ep.addClass('hidden'));
		setShowSettingsBox(true);
	}, [plumbInstance]);

	const closeDialog = useCallback(() => {
		setShowSettingsBox(false);
		plumbInstance?.getEndpoints(gateElRef.current).forEach(ep => ep.removeClass('hidden'));
	}, [plumbInstance]);


	const togglePortsClass = useCallback((className: string, add: boolean) => {
		if (!plumbInstance) { return; }
		
		const ports = plumbInstance.getEndpoints(canvasDomId);
		ports.forEach(port => {
			if (add) {
				port.addClass(className);
			} else {
				port.removeClass(className);
			}
		});
	}, [canvasDomId, plumbInstance]);

	const handleRepaintLabels = useCallback(() => {
		widgetLabelsRef.current?.repaintLabels();
	}, []);

	// Monitors changes to the enable/disable property
	useEffect(() => {
		if (gateElRef.current) {
			togglePortsClass(DISABLED_WIDGET_CLASS, !!gateInfo.disabled);
		}
	}, [gateInfo.disabled, togglePortsClass]);


	const gateHeaderText = (gateUI.getWidgetTitle && gateUI.getWidgetTitle(intl)) || gateInfo.type;
	const groupMarkdownHelp = (gateState as WidgetGroupState).markdownHelp;
	const widgetGroupTitle = type === WidgetType.widgetGroup && groupMarkdownHelp ? (gateState as WidgetGroupState).name : undefined;

	const handleShowHelp = useCallback(() => {
		const isNativeWidget = type !== WidgetType.hubService;
		onShowHelp({
			widgetId: gateId,
			type,
			recipeId,
			thingRecipeId,
			isNativeWidget,
			customMarkdown: groupMarkdownHelp,
			customTitle: widgetGroupTitle
		});
	}, [onShowHelp, gateId, type, recipeId, thingRecipeId, widgetGroupTitle, groupMarkdownHelp]);

	return (
		<>
			<div ref={gateElRef}
				data-kemu-type={gateInfo.type}
				className={classNames(
					CANVAS_WIDGET_CLASS,
					`ctgry-${getCategoryFromType(gateInfo.type)}`,
					
					gateUI.getWrapperClass && gateUI.getWrapperClass(),
					{
						'original-event': gateInfo.returnOriginalEvent,
						'widget-disabled': gateInfo.disabled,
						'settings-visible': showSettingsBox,
						[CUSTOM_HUB_SERVICE_UI_CLASS]: !!hubService?.widgetUI,
						[WIDGET_ABORT_DRAG_SELECTION]: isLockedNote,
						[styles.HiddenWidget]: hidden && type === WidgetType.hubService,
						'widget-run-mode-changed': runModeStatus.shouldHighlight,
						'widget-run-mode-position': runModeStatus.highlightType === 'position',
					},
					...dynamicWrapperClasses,
				)}
				style={gatePos}
				id={canvasDomId}
				onContextMenu={handleItemClick}
			>

				{gateUI.CustomSettingsDialog && gateElRef.current && (
					<div className={
						classNames('gate-settings-wrapper', 'custom-size', { 'visible': showSettingsBox })
					} style={{ position: 'absolute', height: '100%', 'width': '100%' }}>
						<AnimatePresence mode='wait'>
							{showSettingsBox && (
								<motion.div
									className="animator"
									animate="visible"
									initial="hidden"
									variants={settingsDialogAnimation}
									exit="hide"
								>
									<gateUI.CustomSettingsDialog
										repaintExistingPorts={handleRepaintPorts}
										redrawPortsAndConnections={handleRedrawPorts}
										repaintLabels={handleRepaintLabels}
										container={gateElRef.current}
										onClose={closeDialog}
										gateInfo={gateInfo}
										recipeId={props.recipeId}
										recipeType={currentRecipeType}
										blockId={props.thingRecipeId}
									>
										<GateSettingsCloseBtn onClose={closeDialog}/>
									</gateUI.CustomSettingsDialog>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				)}

				<WidgetProcessingWrapper 
					widgetInfo={gateInfo} 
					hidden={hidden}
					recipeId={recipeId}
					thingRecipeId={thingRecipeId}
				>
					<div className={classNames('cg-container', styles.Container)}>
						{gateUI.hasTitle && (
							<div className={`cg-header ${hasCustomSettings ? `custom-settings ${showSettingsBox ? 'visible' : ''}` : ''}`}>
								<div className="cg-label noselect">
									<span>{gateHeaderText}</span>
								</div>
								{hasCustomSettings && (
									<span className="settings-btn" onClick={onShowCustomSettings}>
										<SettingFilled />
									</span>
								)}
							</div>
						)}

						<WidgetHelpBar
							onShowHelp={handleShowHelp}
							className={styles.HelpBar}
							widgetId={gateId}
							widgetType={type}
							widgetState={gateState}
							parentGroupId={gateInfo.groupId}
						/>

						<div className={classNames('cg-body', {
							['hs-container']: type === WidgetType.hubService,
						})}>
							<gateUI.Element
								info={gateInfo}
								hidden={hidden}
								recipeType={currentRecipeType}
								thingRecipeId={props.thingRecipeId}
								thingDbId={props.thingRecipeId}
								thingVersion={props.thingVersion}
								recipeId={props.recipeId}
								repaintExistingPorts={handleRepaintPorts}
								redrawPortsAndConnections={handleRedrawPorts}
								customSettingsVisible={showSettingsBox}
								openCustomSettings={onShowCustomSettings}
								repaintLabels={handleRepaintLabels}
								setDynamicWrapperClasses={setDynamicWrapperClasses}
							/>
							{gateUI.getFloatingLabel && (
								<div className={styles.FloatingLabel}>
									{gateUI.getFloatingLabel(intl, gateState)}
								</div>
							)}
						</div>

						<WidgetLabels
							inputs={gatePorts.inputs}
							outputs={gatePorts.outputs}
							hidden={!!(gateUI.hideLabelsOnSettingsOpen && showSettingsBox) || hidden}
							ref={widgetLabelsRef}
						/>
					</div>
				</WidgetProcessingWrapper>
			</div>
		</>
	);
};

export default CanvasGateElement;
